// Enhanced Scoring System for Crazy Maze
// This replaces the scoring logic in CrazyMazeScene.js

export class EnhancedScoring {
  constructor(difficulty = "medium") {
    this.difficulty = difficulty;
    
    // Difficulty multipliers
    this.difficultyMultipliers = {
  easy: { base: 1.0, time: 1, step: 2, efficiency: 1.2 },
  medium: { base: 1.2, time: 2, step: 5, efficiency: 1.5 },
  hard: { base: 1.5, time: 3, step: 8, efficiency: 2.0 }
};
    
    this.multiplier = this.difficultyMultipliers[difficulty] || this.difficultyMultipliers.medium;
    
    // Scoring constants
    this.BASE_SCORE = 1000;
    this.GEM_BONUS = 50;
    this.GOLDEN_GEM_BONUS = 200;
    this.WALL_PENALTY = 5;
    this.COMPLETION_BONUS = 500;
    
    // Performance thresholds
    this.PERFECT_EFFICIENCY = 1.0;  // player path = optimal path
    this.GOOD_EFFICIENCY = 1.2;     // player path <= 120% of optimal
    this.DECENT_EFFICIENCY = 1.5;   // player path <= 150% of optimal
    
    // Time performance (seconds per step)
    this.SPEED_THRESHOLDS = {
      lightning: 0.5,  // < 0.5s per step
      fast: 1.0,       // < 1s per step  
      normal: 2.0,     // < 2s per step
      slow: 4.0        // < 4s per step
    };
  }

  calculateScore(stats) {
    const {
      gemsCollected = 0,
      goldenGemsCollected = 0,
      playerPathLen = 0,
      shortestPathLen = 1,
      wallHits = 0,
      timeTaken = 0,
      totalGems = 0
    } = stats;

    let score = 0;
    let breakdown = {};

    // 1. Base Score (scaled by difficulty)
    const baseScore = Math.floor(this.BASE_SCORE * this.multiplier.base);
    score += baseScore;
    breakdown.baseScore = baseScore;

    // 2. Completion Bonus
    const completionBonus = Math.floor(this.COMPLETION_BONUS * this.multiplier.base);
    score += completionBonus;
    breakdown.completionBonus = completionBonus;

    // 3. Gem Collection Bonus
    const regularGemBonus = (gemsCollected - goldenGemsCollected) * this.GEM_BONUS;
    const goldenGemBonus = goldenGemsCollected * this.GOLDEN_GEM_BONUS;
    const gemBonus = regularGemBonus + goldenGemBonus;
    score += gemBonus;
    breakdown.gemBonus = { regular: regularGemBonus, golden: goldenGemBonus, total: gemBonus };

    // 4. Gem Collection Rate Bonus (percentage of gems found)
    if (totalGems > 0) {
      const collectionRate = gemsCollected / totalGems;
      const collectionBonus = Math.floor(collectionRate * 300 * this.multiplier.base);
      score += collectionBonus;
      breakdown.collectionBonus = { rate: collectionRate, bonus: collectionBonus };
    }

    // 5. Path Efficiency Analysis
    const efficiency = playerPathLen / shortestPathLen;
    let efficiencyMultiplier = 1.0;
    let efficiencyBonus = 0;
    
    if (efficiency <= this.PERFECT_EFFICIENCY) {
      efficiencyMultiplier = this.multiplier.efficiency;
      efficiencyBonus = Math.floor(200 * this.multiplier.base);
      breakdown.efficiencyRank = "Perfect";
    } else if (efficiency <= this.GOOD_EFFICIENCY) {
      efficiencyMultiplier = 1.3;
      efficiencyBonus = Math.floor(100 * this.multiplier.base);
      breakdown.efficiencyRank = "Excellent";
    } else if (efficiency <= this.DECENT_EFFICIENCY) {
      efficiencyMultiplier = 1.1;
      efficiencyBonus = Math.floor(50 * this.multiplier.base);
      breakdown.efficiencyRank = "Good";
    } else {
      breakdown.efficiencyRank = "Needs Improvement";
    }

    score = Math.floor(score * efficiencyMultiplier) + efficiencyBonus;
    breakdown.efficiency = { 
      ratio: efficiency, 
      multiplier: efficiencyMultiplier, 
      bonus: efficiencyBonus 
    };

    // 6. Speed Performance
    const timePerStep = timeTaken / shortestPathLen;
    let speedBonus = 0;
    let speedRank = "Slow";

    if (timePerStep < this.SPEED_THRESHOLDS.lightning) {
      speedBonus = Math.floor(300 * this.multiplier.base);
      speedRank = "Lightning";
    } else if (timePerStep < this.SPEED_THRESHOLDS.fast) {
      speedBonus = Math.floor(150 * this.multiplier.base);
      speedRank = "Fast";
    } else if (timePerStep < this.SPEED_THRESHOLDS.normal) {
      speedBonus = Math.floor(75 * this.multiplier.base);
      speedRank = "Normal";
    } else if (timePerStep < this.SPEED_THRESHOLDS.slow) {
      speedBonus = Math.floor(25 * this.multiplier.base);
      speedRank = "Casual";
    }

    score += speedBonus;
    breakdown.speed = { 
      timePerStep, 
      rank: speedRank, 
      bonus: speedBonus 
    };

    // 7. Penalties
    const extraSteps = Math.max(0, playerPathLen - shortestPathLen);
    const stepPenalty = extraSteps * this.multiplier.step;
    const wallPenalty = wallHits * this.WALL_PENALTY;
    const timePenalty = Math.floor(timeTaken * this.multiplier.time);

    score -= (stepPenalty + wallPenalty + timePenalty);
    breakdown.penalties = {
      extraSteps: { count: extraSteps, penalty: stepPenalty },
      wallHits: { count: wallHits, penalty: wallPenalty },
      time: { seconds: timeTaken, penalty: timePenalty },
      total: stepPenalty + wallPenalty + timePenalty
    };

    // 8. Bonus Achievements
    let achievements = [];
    
    // Perfect Path Achievement
    if (efficiency === 1.0) {
      const perfectBonus = Math.floor(500 * this.multiplier.base);
      score += perfectBonus;
      achievements.push({ name: "Perfect Navigator", bonus: perfectBonus });
    }
    
    // Speed Demon Achievement
    if (speedRank === "Lightning") {
      const speedDemonBonus = Math.floor(300 * this.multiplier.base);
      score += speedDemonBonus;
      achievements.push({ name: "Speed Demon", bonus: speedDemonBonus });
    }
    
    // Treasure Hunter Achievement (all gems collected)
    if (totalGems > 0 && gemsCollected === totalGems) {
      const treasureBonus = Math.floor(400 * this.multiplier.base);
      score += treasureBonus;
      achievements.push({ name: "Treasure Hunter", bonus: treasureBonus });
    }
    
    // No Damage Achievement
    if (wallHits === 0) {
      const noDamageBonus = Math.floor(200 * this.multiplier.base);
      score += noDamageBonus;
      achievements.push({ name: "Ghost Walker", bonus: noDamageBonus });
    }

    breakdown.achievements = achievements;

    // Ensure minimum score
    const finalScore = Math.max(0, Math.floor(score));
    
    return {
      score: finalScore,
      breakdown,
      grade: this.calculateGrade(finalScore, this.difficulty),
      rank: this.calculateRank(efficiency, speedRank, achievements.length)
    };
  }

  calculateGrade(score, difficulty) {
    const thresholds = {
      easy: { S: 2500, A: 2000, B: 1500, C: 1000, D: 500 },
      medium: { S: 3000, A: 2400, B: 1800, C: 1200, D: 600 },
      hard: { S: 4000, A: 3200, B: 2400, C: 1600, D: 800 }
    };

    const levels = thresholds[difficulty] || thresholds.medium;
    
    if (score >= levels.S) return 'S';
    if (score >= levels.A) return 'A';
    if (score >= levels.B) return 'B';
    if (score >= levels.C) return 'C';
    if (score >= levels.D) return 'D';
    return 'F';
  }

  calculateRank(efficiency, speedRank, achievementCount) {
    if (efficiency === 1.0 && speedRank === "Lightning" && achievementCount >= 3) {
      return "Maze Master";
    } else if (efficiency <= 1.1 && (speedRank === "Lightning" || speedRank === "Fast")) {
      return "Speed Runner";
    } else if (efficiency <= 1.2 && achievementCount >= 2) {
      return "Navigator";
    } else if (efficiency <= 1.5) {
      return "Explorer";
    } else {
      return "Wanderer";
    }
  }

  // Method to get real-time score during gameplay
  getCurrentScore(stats) {
    // Simplified version for real-time updates
    const baseScore = Math.floor(this.BASE_SCORE * this.multiplier.base);
    const gemScore = stats.gemsCollected * this.GEM_BONUS;
    const wallPenalty = stats.wallHits * this.WALL_PENALTY;
    const timePenalty = Math.floor((stats.timeTaken || 0) * this.multiplier.time);
    
    return Math.max(0, baseScore + gemScore - wallPenalty - timePenalty);
  }
}

// Usage in CrazyMazeScene.js:
/*
// At the top of CrazyMazeScene.js, import:
import { EnhancedScoring } from './EnhancedScoring.js';

// In constructor:
this.scoringSystem = new EnhancedScoring(this.difficulty);

// Replace calculateScore method with:
calculateScore(optLen, time) {
  const stats = {
    gemsCollected: this.gemsCollected,
    goldenGemsCollected: this.goldenGemsCollected || 0,
    playerPathLen: this.stepsTaken,
    shortestPathLen: optLen,
    wallHits: this.wallHits,
    timeTaken: time,
    totalGems: this.totalGems || this.gemMap.size
  };
  
  return this.scoringSystem.calculateScore(stats);
}

// For real-time score updates:
maybeEmitScore() {
  if (this.time.now - this.lastScoreEmit > 100) {
    const currentScore = this.scoringSystem.getCurrentScore({
      gemsCollected: this.gemsCollected,
      wallHits: this.wallHits,
      timeTaken: Math.floor((this.time.now - this.startTime) / 1000)
    });
    this.externalEvents?.emit("scoreChanged", currentScore);
    this.lastScoreEmit = this.time.now;
  }
}
*/